import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFacilitySearch } from '../api/facilityPage.ts';
import { LoadFailure } from '../components/LoadFailure.tsx';

/**
 * Find a building by name, another spelling or street (8 October), and open
 * its page. Over `POST /facilities/search`, the same search intake's place picker
 * uses: a building is shared reference data, so every building can be found,
 * whoever is placed there.
 */
export function Facilities() {
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim()), 250);
    return () => clearTimeout(t);
  }, [typed]);
  const search = useFacilitySearch(q);
  const rows = q.length >= 2 ? search.data?.facilities ?? [] : [];

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Facilities</h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">
            Find a building by its name, another name it goes by, or its street, and see who is placed there.
          </p>
          <label className="mt-3 block max-w-xl">
            <span className="sr-only">Find a facility</span>
            <input
              type="search"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Find a facility"
              className="min-h-[44px] w-full rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900"
            />
          </label>
        </header>
        <div className="page-body">
          {q.length < 2 ? (
            <p className="px-4 py-10 text-center text-neutral-500">Type at least two letters.</p>
          ) : search.error && !search.data ? (
            <LoadFailure what="The facility search" verb="could not be run" error={search.error}
                         onRetry={() => void search.refetch()} retrying={search.isFetching} />
          ) : !search.data ? (
            <p className="px-4 py-10 text-center text-neutral-500">Searching…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-10 text-center text-neutral-600">No facility matches “{q}”.</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {rows.map((f) => (
                <li key={f.id}>
                  <Link to={`/facilities/${f.id}`}
                        className="block min-h-[52px] px-4 py-2.5 hover:bg-neutral-50 active:bg-neutral-100 lg:px-5">
                    <p className="font-semibold text-neutral-900">{f.facility_name}</p>
                    <p className="text-sm text-neutral-600">
                      {[f.residence_type_label ?? f.facility_type,
                        [f.street_address, f.city].filter(Boolean).join(', ')].filter(Boolean).join(' · ')
                        || 'No address recorded'}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
